/* eslint-disable max-len */
import {
  OperationPart,
  LaborTime,
  GetEasyBasketOperationDetailResponse,
  PlateLine,
} from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import {
  GetEasyBasketOperationsResponse,
  Operation,
} from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationsResponse';

import {
  GET_EASY_BASKET_OPERATION_DETAILS_RESPONSE,
  GET_EASY_BASKET_OPERATIONS_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { PlateImage } from 'domains/catalog/Catalog.types';
import {
  EASY_BASKET_NAMESPACE,
  EasyBasketState,
  GET_OPERATION_DETAILS_REQUEST,
  GET_OPERATIONS_REQUEST,
  OperationDetail,
  OperationLaborTime,
  OperationReference,
} from 'domains/easyBasket/EasyBasket.types';
import { FOUND, SEARCH_STATUS, SearchData } from 'utils';

export const fetchEasyBasketOperationsRequestSaga = createAction<{
  vehicleKey: string;
  operationsIds: string[];
}>(GET_OPERATIONS_REQUEST);

export const fetchEasyBasketOperationsResponseSaga = createAction(GET_EASY_BASKET_OPERATIONS_RESPONSE);

export const fetchEasyBasketOperationDetailsRequestSaga = createAction<{
  vehicleKey: string;
  operationsIds: string[];
  genericParts: string[];
}>(GET_OPERATION_DETAILS_REQUEST);

export const fetchEasyBasketOperationDetailResponseSaga = createAction(GET_EASY_BASKET_OPERATION_DETAILS_RESPONSE);

// Init state
const initialState: EasyBasketState = {
  operations: new Map<string, SearchData<Operation>>(),
  operationDetails: new Map<string, SearchData<OperationDetail>>(),
  referenceSelection: new Map<string, OperationReference>(),
  referenceSelectionDisabled: new Map<string, OperationReference>(),
  laborTimeSelection: new Map<string, OperationLaborTime>(),
  selectedOperation: '',
};

const slice = createSlice({
  initialState,
  name: EASY_BASKET_NAMESPACE,
  reducers: {
    setInitialState: () => initialState,
    setOperations: (state, { payload }: PayloadAction<GetEasyBasketOperationsResponse>) => {
      const { operations } = payload;
      operations.forEach((operation) => {
        state.operations.set(operation.id, {
          data: operation,
          searchStatus: FOUND,
        });
      });
    },
    setOperationsStatus: (
      state,
      { payload }: PayloadAction<{ operationsIds: string[]; searchStatus: SEARCH_STATUS }>,
    ) => {
      const { operationsIds, searchStatus } = payload;
      operationsIds.forEach((operationId) => {
        state.operations.set(operationId, { searchStatus });
      });
    },
    setOperationsDetails: (state, { payload }: PayloadAction<GetEasyBasketOperationDetailResponse>) => {
      const { operationsDetails } = payload;

      operationsDetails?.forEach((operation) => {
        const partsItems = operation.basketContent.partsItems ?? [];
        const partsPlateLines = new Map<string, PlateLine>();

        partsItems
          .flatMap((item) => item.parts)
          .flatMap((part) => [part, ...(part.alternativeParts ?? [])])
          .forEach((p) => p.plateLines?.forEach((plateLine) => partsPlateLines.set(plateLine.plate.id, plateLine)));

        const mappedOperation: OperationDetail = {
          ...operation,
          basketContent: {
            laborTimeSection: {
              laborTimeItems: operation.basketContent.laborTimeItems ?? [],
            },
            partsSection: {
              partsItems: operation.basketContent.partsItems ?? [],
              plateLines: partsPlateLines ?? [],
            },
          },
        };

        state.operationDetails.set(operation.id, {
          data: mappedOperation,
          searchStatus: FOUND,
        });
      });
    },
    setOperationsDetailsStatus: (
      state,
      { payload }: PayloadAction<{ operationsIds: string[]; searchStatus: SEARCH_STATUS }>,
    ) => {
      const { operationsIds, searchStatus } = payload;
      operationsIds.forEach((operationId) => {
        state.operationDetails.set(operationId, { searchStatus });
      });
    },
    setSelectedOperationId: (state, { payload }: PayloadAction<{ operationId: string }>) => {
      const { operationId } = payload;
      state.selectedOperation = operationId;
    },
    addReferenceSelection: (
      state,
      { payload }: PayloadAction<{ itemGroup: string; reference: OperationReference }>,
    ) => {
      const { itemGroup, reference } = payload;
      if (reference.price) {
        state.referenceSelection.set(itemGroup, { ...reference });
      } else {
        state.referenceSelectionDisabled.set(itemGroup, { ...reference });
      }
    },
    removeReferenceSelection: (state, { payload }: PayloadAction<{ itemGroup: string }>) => {
      const { itemGroup } = payload;
      state.referenceSelection.delete(itemGroup);
    },
    resetReferenceSelection: (state) => {
      state.referenceSelection = new Map();
    },
    addLaborTimeSelection: (
      state,
      { payload }: PayloadAction<{ itemGroup: string; laborTime: OperationLaborTime }>,
    ) => {
      const { itemGroup, laborTime } = payload;
      state.laborTimeSelection.set(itemGroup, { ...laborTime });
    },
    removeLaborTimeSelection: (state, { payload }: PayloadAction<{ itemGroup: string }>) => {
      const { itemGroup } = payload;
      state.laborTimeSelection.delete(itemGroup);
    },
    resetLaborTimeSelection: (state) => {
      state.laborTimeSelection = new Map();
    },
  },
});

export const {
  setInitialState,
  setOperations,
  setOperationsStatus,
  setOperationsDetails,
  setOperationsDetailsStatus,
  setSelectedOperationId,
  removeReferenceSelection,
  addReferenceSelection,
  resetReferenceSelection,
  addLaborTimeSelection,
  removeLaborTimeSelection,
  resetLaborTimeSelection,
} = slice.actions;

export const getEasyBasketOperations = createSelector(
  (state: RootState) => state.easyBasket.operations,
  (operations) => operations,
);

export const getEasyBasketOperationsByIds = createSelector(
  (state: RootState) => state.easyBasket.operations,
  (_state: RootState, operationsIds: string[]) => operationsIds,
  (operations, operationsIds) => {
    const filteredOperations = new Map<string, SearchData<Operation>>();
    operationsIds.forEach((id) => {
      const operation = operations.get(id);
      if (operation) {
        filteredOperations.set(id, operation);
      }
    });
    return filteredOperations;
  },
);

export const getEasyBasketSelectedOperationId = createSelector(
  (state: RootState) => state.easyBasket.selectedOperation,
  (selectedOperation) => selectedOperation,
);

export const getEasyBasketOperation = createSelector(
  (state: RootState, operationId: string) => state.easyBasket.operations.get(operationId),
  (operation) => operation,
);

export const getSelectedEasyBasketOperation = createSelector(
  getEasyBasketSelectedOperationId,
  (state: RootState) => state.easyBasket.operations,
  (selectedOperationId, operations) => operations.get(selectedOperationId),
);

export const getEasyBasketOperationIdsSortedFiltered = createSelector(
  (_state: RootState, operationIds: string[]) => operationIds,
  (state: RootState) => state.easyBasket.operations,
  (operationIds, operations) => {
    const existing = operationIds.filter((id) => {
      const optData = operations.get(id)?.data;
      return optData?.designation && optData?.maturity === 'OFFICIAL';
    });
    existing.sort((a, b) => {
      const aDes = (operations.get(a)?.data?.designation ?? '').toLowerCase();
      const bDes = (operations.get(b)?.data?.designation ?? '').toLowerCase();
      if (aDes < bDes) {
        return -1;
      }
      if (aDes > bDes) {
        return 1;
      }
      return 0;
    });
    const missing = operationIds.filter((id) => !operations.get(id)?.data?.designation);
    return [...existing, ...missing];
  },
);

export const getEasyBasketOperationDetails = createSelector(
  (state: RootState, operationId: string) => state.easyBasket.operationDetails.get(operationId),
  (operation) => operation,
);

export const getAllEasyBasketOperationIds = createSelector(
  (state: RootState) => state.easyBasket.operationDetails,
  (operationDetails) => Array.from(operationDetails.keys()),
);

export const getEasyBasketSelectedReferencesMap = createSelector(
  (state: RootState) => state.easyBasket.referenceSelection,
  (referenceSelection) => referenceSelection,
);

export const getEasyBasketSelectedLaborTimesMap = createSelector(
  (state: RootState) => state.easyBasket.laborTimeSelection,
  (laborTimeSelection) => laborTimeSelection,
);

export const getEasyBasketSelectedDisabledReferencesMap = createSelector(
  (state: RootState) => state.easyBasket.referenceSelectionDisabled,
  (referenceSelectionDisabled) => referenceSelectionDisabled,
);

export const getEasyBasketPlateImage = createSelector(
  (state: RootState) => state.easyBasket.operationDetails,
  (
    _state: RootState,
    params: {
      plateId: string | undefined;
      operationId: string | undefined;
    },
  ) => params,
  (operationDetails, { operationId, plateId }) => {
    if (!operationId || !plateId) {
      return undefined;
    }

    const operationDetail = operationDetails.get(operationId)?.data;
    if (!operationDetail) {
      return undefined;
    }

    const plateLine = operationDetail.basketContent.partsSection.plateLines.get(plateId);
    if (!plateLine) {
      return undefined;
    }

    return {
      plateId: plateLine.plate.id,
      imageKey: plateLine.plate.thumbnailImageKey,
    } as PlateImage;
  },
);

export const getSelectedReference = createSelector(
  (state: RootState) => state.easyBasket.referenceSelection,
  (_state: RootState, itemGroup: string) => itemGroup,
  (referenceSelection, itemGroup) => referenceSelection.get(itemGroup),
);

export const getSelectedReferencesMap = createSelector(
  (state: RootState) => state.easyBasket.referenceSelection,
  (referenceSelection) => referenceSelection,
);

export const getEasyBasketReference = createSelector(
  (state: RootState) => state.easyBasket,
  (
    _state: RootState,
    params: {
      itemGroup: string;
      mainReferenceNumber: string;
      referenceNumber: string;
    },
  ) => params,
  (easyBasket, params) => {
    const { itemGroup, mainReferenceNumber, referenceNumber } = params;
    const isAlternative = referenceNumber !== mainReferenceNumber;

    const detailSearch = easyBasket.operationDetails.get(easyBasket.selectedOperation);

    const partGroup = detailSearch?.data?.basketContent.partsSection.partsItems.find(
      (group) => group.itemGroup === itemGroup,
    );
    const mainReference = partGroup?.parts.find((p) => p.reference === mainReferenceNumber);
    if (!isAlternative) {
      return mainReference;
    }
    return mainReference?.alternativeParts?.find((p) => p.reference === referenceNumber);
  },
);

export const getSelectedLaborTime = createSelector(
  (state: RootState) => state.easyBasket.laborTimeSelection,
  (_state: RootState, itemGroup: string) => itemGroup,
  (laborTimeSelection, itemGroup) => laborTimeSelection.get(itemGroup),
);

export const getSelectedLaborTimes = createSelector(
  (state: RootState) => state.easyBasket.laborTimeSelection,
  (laborTimeSelection) => laborTimeSelection,
);

export const getSelectedEasyBasketReferencesData = createSelector(
  (state: RootState) => state.easyBasket.referenceSelection,
  (state: RootState) => state.easyBasket.operationDetails,
  (state: RootState) => state.easyBasket.selectedOperation,
  (referenceSelection, operationDetails, selectedOperation) => {
    const partsItems = operationDetails.get(selectedOperation)?.data?.basketContent.partsSection.partsItems;
    if (!partsItems) {
      return [];
    }

    return Array.from(referenceSelection.entries())
      .map(([itemGroup, { referenceNumber, quantity }]) => {
        const allParts = partsItems
          .filter((item) => item.itemGroup === itemGroup)
          .flatMap((item) => item.parts)
          .flatMap((part) => [part, ...(part.alternativeParts ?? [])]);

        const selectedPart = allParts.find((p) => p.reference === referenceNumber);
        return selectedPart ? ({ ...selectedPart, quantity: quantity.toString() } as OperationPart) : undefined;
      })
      .filter((part): part is OperationPart => part !== undefined);
  },
);

export const getSelectedEasyBasketLaborTimesData = createSelector(
  (state: RootState) => state.easyBasket.laborTimeSelection,
  (state: RootState) => state.easyBasket.operationDetails,
  (state: RootState) => state.easyBasket.selectedOperation,
  (laborTimeSelection, operationDetails, selectedOperation) => {
    const selectedOperationDetail = operationDetails.get(selectedOperation)?.data;
    if (!selectedOperationDetail) {
      return [];
    }

    const laborTimeItems = selectedOperationDetail.basketContent.laborTimeSection.laborTimeItems;
    return Array.from(laborTimeSelection.entries())
      .map(([itemGroup, selectedLaborTime]) => {
        const laborTimeItem = laborTimeItems.find((item) => item.itemGroup === itemGroup);
        return laborTimeItem?.laborTimes.find((lt) => lt.id === selectedLaborTime.laborTimeId);
      })
      .filter((laborTime): laborTime is LaborTime => laborTime !== undefined);
  },
);

export const getSelectedEasyBasketReferenceNumbers = createSelector(
  (state: RootState) => state.easyBasket.referenceSelection,
  (referenceSelection) => Array.from(referenceSelection.values()).map((ref) => ref.referenceNumber),
);

// Export reducer
export default slice.reducer;
